// sections/Pipeline.tsx
import React, { useRef, useState } from 'react';
import { motion } from "framer-motion";
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { Points as ThreePoints } from 'three';
import * as random from 'maath/random/dist/maath-random.esm';


// DNA-inspired floating particles component
const FloatingDNAs = () => {
  const ref = useRef<ThreePoints>(null!);
  const particles = React.useMemo(() => {
    return random.inSphere(new Float32Array(2000), { radius: 5 });
  }, []);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.02;
      ref.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });

  return (
    
    <Points ref={ref} positions={particles} stride={3}>
      <PointMaterial
        transparent
        color="#60a5fa" // Blue color for DNA-like appearance
        size={0.08}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </Points>
  );
};

const stageLabels = ["Discovery", "Pre-Clinical", "Pre-IND", "IND", "Phase 1", "Phase 2", "Phase 3"];

const oncologyData = [
  {
    indication: "Triple-Negative Breast Cancer (TNBC)",
    target: "c-MYC",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Pancreatic Cancer",
    target: "c-MYC",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Ovarian Cancer",
    target: "c-MYC",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Colon Cancer",
    target: "c-MYC",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "HER2+ EGFR T790M NSCLC",
    target: "HER2/ERBB2",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: true,
      phase1: false,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "HER2+ EGFR ex 19 del NSCLC",
    target: "HER2/ERBB2",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: true,
      phase1: false,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Metastatic HER2+ Resistant Breast Cancer (mBCa)",
    target: "HER2/ERBB2",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: true,
      phase1: false,
      phase2: false,
      phase3: false
    }
  }
];

const pediatricData = [
  {
    indication: "Rhabdomyosarcoma",
    target: "MYCN",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Neuroblastoma",
    target: "MYCN",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Retinoblastoma",
    target: "MYCN",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: true,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Osteosarcoma",
    target: "c-MYC",
    stages: {
      discovery: false,
      preclinical: true,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  }
];

// Hippo/YAP Pathway Data
const hippoYapData = [
  {
    indication: "TNBC (African women)",
    target: "TEAD1/YAP1",
    stages: {
      discovery: false,
      preclinical: false,
      preIND: false,
      IND: true,
      phase1: false,
      phase2: false,
      phase3: false
    }
  }
];

// Metabolic Disease Data (new)
const metabolicData = [
  {
    indication: "NASH (MASH)",
    target: "TEAD1/YAP1",
    stages: {
      discovery: false,
      preclinical: true,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "ALD / NAFLD",
    target: "TEAD1/YAP1",
    stages: {
      discovery: false,
      preclinical: true,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Steatohepatitis, Obesity",
    target: "TEAD1/YAP1",
    stages: {
      discovery: false,
      preclinical: true,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  }
];

// Neurodegenerative Data (new)
const neuroData = [
  {
    indication: "ALS / FTD",
    target: "UNDISCLOSED",
    stages: {
      discovery: true,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  }
];

// Rare Disease Data (new)
const rareDiseaseData = [
  {
    indication: "FOXG1 Syndrome",
    target: "FOXG1",
    stages: {
      discovery: true,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  },
  {
    indication: "Gangliosidosis",
    target: "HEXA",
    stages: {
      discovery: true,
      preclinical: false,
      preIND: false,
      IND: false,
      phase1: false,
      phase2: false,
      phase3: false
    }
  }
];

// Enhanced PipelineTable component with indicator options
function PipelineTable({ 
  title, 
  description, 
  data, 
  color,
  indicator = 'dot' // 'dot' or 'arrow'
}: {
  title: string;
  description: string;
  data: typeof oncologyData;
  color: 'purple' | 'blue' | 'green' | 'orange' | 'indigo';
  indicator?: 'dot' | 'arrow';
}) {
  // Color mapping with additional options
  const colorClasses = {
    purple: {
      text: 'text-purple-700',
      border: 'border-purple-200',
      bgHover: 'hover:bg-purple-50',
      active: 'bg-purple-600'
    },
    blue: {
      text: 'text-blue-700',
      border: 'border-blue-200',
      bgHover: 'hover:bg-blue-50',
      active: 'bg-blue-600'
    },
    green: {
      text: 'text-green-700',
      border: 'border-green-200',
      bgHover: 'hover:bg-green-50',
      active: 'bg-green-600'
    },
    orange: {
      text: 'text-orange-700',
      border: 'border-orange-200',
      bgHover: 'hover:bg-orange-50',
      active: 'bg-orange-600'
    },
    indigo: {
      text: 'text-indigo-700',
      border: 'border-indigo-200',
      bgHover: 'hover:bg-indigo-50',
      active: 'bg-indigo-600'
    }
  };

  const currentColor = colorClasses[color];

  return (
    
    <div className="mb-20">
     
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="max-w-7xl mx-auto text-center"
      >
        <h2 className={`text-4xl md:text-5xl font-bold ${currentColor.text} mb-6`}>
          {title}
        </h2>
        <p className="text-lg md:text-xl text-white mb-12">
          {description}
        </p>

        <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead>
              <tr className={`text-left text-sm ${currentColor.text} uppercase border-b-2 ${currentColor.border}`}>
                <th className="p-4 pl-6">Indication</th>
                <th className="p-4">Target (Genes)</th>
                {stageLabels.map((label) => (
                  <th key={label} className="p-4 text-center">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((item, idx) => (
                <tr
                  key={idx}
                  className={`text-sm text-gray-800 ${currentColor.bgHover} transition-colors`}
                >
                  <td className="p-4 pl-6 font-medium">{item.indication}</td>
                  <td className={`p-4 font-semibold ${currentColor.text}`}>
                    {item.target || "-"}
                  </td>
                  {Object.values(item.stages).map((isActive, i) => (
                    <td key={i} className="p-4 text-center">
                      {indicator === 'dot' ? (
                        <div className={`w-6 h-6 rounded-full mx-auto ${
                          isActive ? `${currentColor.active} animate-pulse` : 'bg-gray-200'
                        }`}></div>
                      ) : (
                        <div className="text-2xl">
                          {isActive ? '▼' : '-'}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}

const pipelineSections = [
  {
    id: 'oncology',
    title: "Oncology",
    description: "Advancing targeted therapies across multiple cancer indications",
    data: oncologyData,
    color: "purple"
  },
  {
    id: 'pediatric',
    title: "Pediatric Oncology",
    description: "Advancing therapies for childhood cancers",
    data: pediatricData,
    color: "blue"
  },
  {
    id: 'hippo',
    title: "Hippo/YAP Pathway",
    description: "Targeting the TEAD1/YAP1 pathway in specific populations",
    data: hippoYapData,
    color: "green"
  },
  {
    id: 'metabolic',
    title: "Metabolic Disease",
    description: "Developing treatments for liver and metabolic disorders",
    data: metabolicData,
    color: "orange"
  },
  {
    id: 'neuro',
    title: "Neurodegenerative",
    description: "Pursuing therapies for neurological conditions",
    data: neuroData,
    color: "indigo"
  },
  {
    id: 'rare',
    title: "Rare Disease",
    description: "Addressing unmet needs in rare genetic disorders",
    data: rareDiseaseData,
    color: "purple",
    indicator: "dot"
  }
];

export default function Pipeline() {
  const [activeTab, setActiveTab] = useState('oncology');

  return (
    <section id="pipeline" className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-900 to-blue-900 py-20 px-4 md:px-16">
      {/* DNA Background Animation - no external dependencies */}
      <div className="absolute inset-0 opacity-10">
        <Canvas camera={{ position: [0, 0, 10], fov: 75 }}>
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={0.5} />
          <FloatingDNAs />
        </Canvas>
      </div>

      {/* Pure CSS background elements instead of SVGs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i}
              className="absolute w-0.5 h-16 bg-blue-400"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                transform: `rotate(${Math.random() * 360}deg)`
              }}
            />
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="relative max-w-7xl mx-auto z-10"
      >
        <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-300 to-emerald-300">
          Therapeutic Pipelines
        </h2>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {pipelineSections.map((section) => (
            <motion.button
              key={section.id}
              onClick={() => setActiveTab(section.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === section.id 
                  ? `bg-${section.color}-600 text-white shadow-md`
                  : `bg-white/10 text-gray-200 hover:bg-white/20 border border-white/20`
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {section.title}
            </motion.button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-white/20">
          {pipelineSections.map((section) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: activeTab === section.id ? 1 : 0,
                height: activeTab === section.id ? 'auto' : 0,
                display: activeTab === section.id ? 'block' : 'none'
              }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === section.id && (
                <PipelineTable
                  title={section.title}
                  description={section.description}
                  data={section.data}
                  color={section.color as any}
                  indicator={section.indicator as any}
                />
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Additional decorative elements */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-blue-900/80 to-transparent z-0"></div>
    </section>
  );
}